import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Loader2, Plus, FileText, Eye, Edit, Trash2, CheckCircle, XCircle, Download } from "lucide-react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import QRCode from "react-qr-code";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { format } from "date-fns";
import { sk } from "date-fns/locale";
import { CreditNotePreview } from "@/components/ui/credit-note-preview";
import type { Client, Dobropis, Invoice, InsertDobropis } from "@shared/schema";

const dobropisSchema = z.object({
  clientId: z.number().int().positive("Vyberte klienta"),
  clientName: z.string().min(1, "Meno klienta je povinné"),
  originalInvoiceNumber: z.string().optional(),
  reasonType: z.enum(["storno_objednavky", "vratenie_tovaru", "ine"]).default("storno_objednavky"),
  reason: z.string().min(1, "Dôvod dobropisu je povinný"),
  amount: z.number().min(0.01, "Suma musí byť väčšia ako 0"),
  vatRate: z.number().min(0).default(20),
  totalAmount: z.number().min(0.01, "Celková suma musí byť väčšia ako 0"),
  status: z.enum(["draft", "sent", "processed", "cancelled"]).default("draft"),
  notes: z.string().optional(),
});

type DobropisFormData = z.infer<typeof dobropisSchema>;

export default function DobropisyPage() {
  const [selectedDobropis, setSelectedDobropis] = useState<Dobropis | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [selectedReasonType, setSelectedReasonType] = useState<string>("storno_objednavky");
  const { toast } = useToast();
  
  // Referencia na PDF preview element
  const dobropisPreviewRef = useState<HTMLDivElement | null>(null);
  
  // PDF export funkcia
  const handlePDFExport = async () => {
    if (!selectedDobropis || !dobropisPreviewRef[0]) return;
    
    try {
      const element = document.getElementById('dobropis-preview');
      if (!element) return;
      
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        onclone: (clonedDoc) => {
          const clonedElement = clonedDoc.getElementById('dobropis-preview');
          if (clonedElement) {
            clonedElement.style.backgroundColor = '#ffffff';
            
            // Uistíme sa, že všetky sivé pozadia sú správne zobrazené
            const grayBgs = clonedElement.querySelectorAll('.bg-gray-50, .bg-gray-100');
            grayBgs.forEach((el: Element) => {
              if (el instanceof HTMLElement) {
                el.style.backgroundColor = '#f9fafb';
              }
            });
          }
        }
      });
      
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
        compress: true,
        putOnlyUsedFonts: true,
        floatPrecision: 16
      });
      
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      
      const imgData = canvas.toDataURL('image/jpeg', 1.0);
      
      pdf.addImage(imgData, 'JPEG', 0, 0, pageWidth, pageHeight, undefined, 'FAST');
      
      const fileName = `Dobropis-${selectedDobropis.creditNoteNumber}.pdf`;
      pdf.save(fileName);
      
      toast({
        title: 'Export PDF',
        description: `Dobropis bol úspešne exportovaný ako ${fileName}`,
      });
    } catch (error) {
      console.error('Chyba pri vytváraní PDF:', error);
      toast({
        title: 'Chyba',
        description: 'Nastala chyba pri vytváraní PDF súboru',
        variant: 'destructive',
      });
    }
  };

  // Načítanie používateľských údajov pre DPH nastavenia
  const { data: userData } = useQuery({
    queryKey: ["/api/user"],
  });

  const form = useForm<DobropisFormData>({
    resolver: zodResolver(dobropisSchema),
    defaultValues: {
      clientName: "",
      originalInvoiceNumber: "",
      reasonType: "storno_objednavky",
      reason: "Storno objednávky",
      amount: 0,
      vatRate: 20,
      totalAmount: 0,
      status: "draft",
      notes: "",
    },
  });

  // Načítanie dobropisov
  const { data: dobropisy, isLoading } = useQuery({
    queryKey: ["/api/dobropisy"],
  });

  // Načítanie klientov
  const { data: clients } = useQuery({
    queryKey: ["/api/clients"],
    queryFn: async () => {
      const response = await apiRequest('GET', '/api/clients');
      const data = await response.json();
      return data.items || data;
    }
  });

  // Načítanie faktúr - zobrazia sa len faktúry vybraného klienta
  const selectedClientId = form.watch("clientId");
  const { data: invoices, refetch: refetchInvoices } = useQuery({
    queryKey: ["/api/invoices", selectedClientId],
    queryFn: async () => {
      console.log('🔧 [Dobropisy] Fetching invoices for client:', selectedClientId);
      const response = await apiRequest('GET', '/api/invoices');
      const data = await response.json();
      const allInvoices = data.data || data.items || data || [];
      console.log('🔧 [Dobropisy] All invoices from API:', allInvoices.length);
      
      // Filtrujeme podľa vybraného klienta, ak je vybraný
      if (selectedClientId) {
        const filtered = allInvoices.filter((inv: any) => {
          const invoiceClientId = inv.clientId || inv.client_id;
          const matches = invoiceClientId === selectedClientId || invoiceClientId === parseInt(selectedClientId);
          console.log('🔧 [Dobropisy] Invoice:', inv.invoiceNumber || inv.invoice_number, 'clientId:', invoiceClientId, 'matches:', matches);
          return matches;
        });
        console.log('🔧 [Dobropisy] Filtered invoices:', filtered.length);
        return filtered;
      }
      return allInvoices;
    },
    enabled: isCreateDialogOpen, // Načítaj len keď je otvorený dialog
  });

  // Refetch faktúr keď sa zmení vybraný klient
  useEffect(() => {
    if (selectedClientId && isCreateDialogOpen) {
      refetchInvoices();
    }
  }, [selectedClientId, isCreateDialogOpen, refetchInvoices]);

  // Nastavenie defaultných hodnôt pre DPH na základe používateľských údajov
  useEffect(() => {
    if (userData && typeof userData === 'object') {
      const defaultVatRate = (userData as any).isVatPayer ? ((userData as any).companyVatRate || 20) : 0;
      form.setValue("vatRate", defaultVatRate);
    }
  }, [userData, form]);

  // Mutácia pre vytvorenie dobropisu
  const createDobropisMutation = useMutation({
    mutationFn: async (data: DobropisFormData) => {
      const response = await apiRequest("POST", "/api/dobropisy", data);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/dobropisy"] });
      setIsCreateDialogOpen(false);
      form.reset();
      toast({
        title: "Dobropis vytvorený",
        description: "Dobropis bol úspešne vytvorený",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Informácia",
        description: error.message || "Nepodarilo sa vytvoriť dobropis",
        variant: "default",
      });
    },
  });

  // Mutácia pre aktualizáciu statusu
  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: number; status: string }) => {
      const response = await apiRequest("PATCH", `/api/dobropisy/${id}/status`, { status });
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Status aktualizovaný",
        description: "Status dobropisu bol úspešne aktualizovaný",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/dobropisy"] });
    },
    onError: (error: any) => {
      toast({
        title: "Informácia",
        description: error.message || "Nepodarilo sa aktualizovať status",
        variant: "default",
      });
    },
  });

  // Mutácia pre mazanie dobropisu
  const deleteDobropisMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiRequest("DELETE", `/api/dobropisy/${id}`);
    },
    onSuccess: () => {
      toast({
        title: "Dobropis zmazaný",
        description: "Dobropis bol úspešne zmazaný",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/dobropisy"] });
    },
    onError: (error: any) => {
      toast({
        title: "Informácia",
        description: error.message || "Nepodarilo sa zmazať dobropis",
        variant: "default",
      });
    },
  });

  const handleSubmit = form.handleSubmit((data) => {
    createDobropisMutation.mutate(data);
  });

  const handleAmountChange = (amount: number) => {
    const isVatPayer = (userData as any)?.isVatPayer || false;
    const vatRate = isVatPayer ? (form.getValues("vatRate") || (userData as any)?.companyVatRate || 20) : 0;
    const totalAmount = isVatPayer ? amount * (1 + vatRate / 100) : amount;
    
    form.setValue("amount", amount);
    form.setValue("totalAmount", totalAmount);
  };

  const handleVatRateChange = (vatRate: number) => {
    const isVatPayer = (userData as any)?.isVatPayer || false;
    const amount = form.getValues("amount") || 0;
    const totalAmount = isVatPayer ? amount * (1 + vatRate / 100) : amount;
    
    form.setValue("vatRate", vatRate);
    form.setValue("totalAmount", totalAmount);
  };

  const handleReasonTypeChange = (reasonType: string) => {
    setSelectedReasonType(reasonType);
    form.setValue("reasonType", reasonType as any);
    
    // Nastavenie defaultného textu podľa typu
    switch (reasonType) {
      case "storno_objednavky":
        form.setValue("reason", "Storno objednávky");
        break;
      case "vratenie_tovaru":
        form.setValue("reason", "Vrátenie tovaru");
        break;
      case "ine":
        form.setValue("reason", "");
        break;
      default:
        form.setValue("reason", "");
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "draft":
        return <Badge variant="secondary">Koncept</Badge>;
      case "issued":
        return <Badge variant="default">Vydaný</Badge>;
      case "approved":
        return <Badge variant="success">Schválený</Badge>;
      case "rejected":
        return <Badge variant="destructive">Zamietnutý</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getClientName = (dobropis: any) => {
    // Ak má dobropis priamo client objekt, použij ho
    if (dobropis?.client?.businessName || dobropis?.client?.name) {
      return dobropis.client.businessName || dobropis.client.name;
    }
    // Fallback na clients array
    if (dobropis?.clientId) {
      const client = Array.isArray(clients) ? clients.find((c: any) => c.id === dobropis.clientId) : null;
      return client?.businessName || client?.name || "Neznámy klient";
    }
    return "Neznámy klient";
  };

  const getInvoiceNumber = (dobropis: any) => {
    // Ak má dobropis priamo invoice objekt, použij ho
    if (dobropis?.invoice?.invoiceNumber) {
      return dobropis.invoice.invoiceNumber;
    }
    // Fallback na invoices array
    if (dobropis?.relatedInvoiceId) {
      const invoice = (invoices as any)?.find((i: any) => i.id === dobropis.relatedInvoiceId);
      return invoice?.invoiceNumber || invoice?.invoice_number || "-";
    }
    return "-";
  };

  const formatInvoiceAmount = (amount: any) => {
    if (!amount) return "0.00";
    const numAmount = typeof amount === 'number' ? amount : parseFloat(amount);
    return isNaN(numAmount) ? "0.00" : numAmount.toFixed(2);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 section-spacing">
        <div>
          <h1 className="page-title">Dobropisy</h1>
          <p className="text-muted-foreground text-lg">Spravujte svoje dobropisy a opravy faktúr</p>
        </div>
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Nový dobropis
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Vytvoriť nový dobropis</DialogTitle>
              <DialogDescription>
                Vyplňte údaje pre vytvorenie nového dobropisu
              </DialogDescription>
            </DialogHeader>
            <Form {...form}>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid gap-4 sm:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="clientId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Klient</FormLabel>
                        <Select 
                          onValueChange={(value) => {
                            const clientId = parseInt(value);
                            field.onChange(clientId);
                            const client = (clients as any)?.find((c: any) => c.id === clientId);
                            if (client) {
                              form.setValue("clientName", client.businessName || client.name);
                              // Refetch faktúr pre nového klienta
                              refetchInvoices();
                            }
                          }}
                          value={field.value?.toString()}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Vyberte klienta" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {(clients as any)?.length > 0 ? (clients as any).map((client: any) => (
                              <SelectItem key={client.id} value={client.id.toString()}>
                                {client.businessName || client.name}
                              </SelectItem>
                            )) : (
                              <SelectItem value="no-clients" disabled>Žiadni klienti</SelectItem>
                            )}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="originalInvoiceNumber"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Pôvodná faktúra</FormLabel>
                        <Select 
                          onValueChange={(value) => {
                            const selectedInvoice = (invoices as any)?.find((inv: any) => 
                              (inv.invoiceNumber || inv.invoice_number) === value
                            );
                            if (selectedInvoice) {
                              field.onChange(value);
                              // Automaticky nastaviť klienta, ak ešte nie je nastavený
                              const invoiceClientId = selectedInvoice.clientId || selectedInvoice.client_id;
                              if (!form.getValues("clientId") && invoiceClientId) {
                                form.setValue("clientId", invoiceClientId);
                                const client = (clients as any)?.find((c: any) => c.id === invoiceClientId);
                                if (client) {
                                  form.setValue("clientName", client.businessName || client.name);
                                }
                              }
                              // Nastaviť sumu na základe faktúry (možno celú sumu alebo časť)
                              const invoiceAmount = selectedInvoice.totalAmount || selectedInvoice.total_amount;
                              if (invoiceAmount) {
                                const amount = typeof invoiceAmount === 'number' ? invoiceAmount : parseFloat(invoiceAmount);
                                form.setValue("amount", amount);
                                handleAmountChange(amount);
                              }
                            }
                          }}
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Vyberte faktúru" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {selectedClientId ? (
                              (invoices as any)?.length > 0 ? (invoices as any)
                                .map((invoice: any) => {
                                  const invoiceNumber = invoice.invoiceNumber || invoice.invoice_number;
                                  const totalAmount = invoice.totalAmount || invoice.total_amount || invoice.total_with_tax;
                                  return (
                                    <SelectItem key={invoice.id} value={invoiceNumber}>
                                      {invoiceNumber} - {formatInvoiceAmount(totalAmount)}€
                                    </SelectItem>
                                  );
                                }) : (
                                  <SelectItem value="no-invoices" disabled>Žiadne faktúry pre tohto klienta</SelectItem>
                                )
                            ) : (
                              <SelectItem value="select-client-first" disabled>Najprv vyberte klienta</SelectItem>
                            )}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="space-y-4">
                  <FormField
                    control={form.control}
                    name="reasonType"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Typ dôvodu</FormLabel>
                        <Select 
                          onValueChange={handleReasonTypeChange}
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Vyberte typ dôvodu" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="storno_objednavky">Storno objednávky</SelectItem>
                            <SelectItem value="vratenie_tovaru">Vrátenie tovaru</SelectItem>
                            <SelectItem value="ine">Iné</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="reason"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Dôvod dobropisu</FormLabel>
                        <FormControl>
                          {selectedReasonType === "ine" ? (
                            <Textarea 
                              placeholder="Zadajte vlastný dôvod pre vytvorenie dobropisu"
                              {...field}
                            />
                          ) : (
                            <Input 
                              placeholder="Automaticky nastavené"
                              {...field}
                              readOnly
                              className="bg-gray-50"
                            />
                          )}
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid gap-4 sm:grid-cols-3">
                  <FormField
                    control={form.control}
                    name="amount"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Suma bez DPH (€)</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            step="0.01"
                            {...field}
                            onChange={(e) => {
                              const value = parseFloat(e.target.value) || 0;
                              field.onChange(value);
                              handleAmountChange(value);
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {(userData as any)?.isVatPayer && (
                    <FormField
                      control={form.control}
                      name="vatRate"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Sadzba DPH (%)</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              step="0.01"
                              {...field}
                              onChange={(e) => {
                                const value = parseFloat(e.target.value) || 0;
                                field.onChange(value);
                                handleVatRateChange(value);
                              }}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}

                  <FormField
                    control={form.control}
                    name="totalAmount"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          {(userData as any)?.isVatPayer ? 'Celková suma s DPH (€)' : 'Celková suma (€)'}
                        </FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            step="0.01"
                            {...field}
                            readOnly
                            className="bg-gray-50"
                            onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="notes"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Poznámky (voliteľné)</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Dodatočné poznámky..."
                          className="min-h-[80px]"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="flex justify-end space-x-2">
                  <Button type="button" variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                    Zrušiť
                  </Button>
                  <Button type="submit" disabled={createDobropisMutation.isPending}>
                    {createDobropisMutation.isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Vytvára sa...
                      </>
                    ) : (
                      "Vytvoriť dobropis"
                    )}
                  </Button>
                </div>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Zoznam dobropisov</CardTitle>
        </CardHeader>
        <CardContent>
          {(dobropisy as any)?.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Žiadne dobropisy zatiaľ neboli vytvorené</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Číslo</TableHead>
                  <TableHead>Klient</TableHead>
                  <TableHead>Faktúra</TableHead>
                  <TableHead>Suma</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Dátum</TableHead>
                  <TableHead>Akcie</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(dobropisy as any)?.length > 0 ? (dobropisy as any).map((dobropis: any) => {
                  // Normalize data - support both snake_case and camelCase
                  const creditNoteNumber = dobropis.creditNoteNumber || dobropis.credit_note_number || '';
                  const totalAmount = dobropis.totalAmount || dobropis.total_amount || 0;
                  const issueDate = dobropis.issueDate || dobropis.issue_date;
                  const status = dobropis.status || 'draft';
                  
                  // Format amount
                  const formattedAmount = typeof totalAmount === 'string' 
                    ? parseFloat(totalAmount).toFixed(2) 
                    : Number(totalAmount).toFixed(2);
                  
                  return (
                    <TableRow key={dobropis.id}>
                      <TableCell className="font-medium">
                        {creditNoteNumber || '-'}
                      </TableCell>
                      <TableCell>{getClientName(dobropis)}</TableCell>
                      <TableCell>{getInvoiceNumber(dobropis)}</TableCell>
                      <TableCell>€{formattedAmount}</TableCell>
                      <TableCell>{getStatusBadge(status)}</TableCell>
                      <TableCell>
                        {issueDate ? format(new Date(issueDate), "dd.MM.yyyy", { locale: sk }) : '-'}
                      </TableCell>
                    <TableCell>
                      <div className="flex space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedDobropis(dobropis);
                            setIsViewDialogOpen(true);
                          }}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        {dobropis.status === "draft" && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => updateStatusMutation.mutate({ id: dobropis.id, status: "issued" })}
                            >
                              <CheckCircle className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => deleteDobropisMutation.mutate(dobropis.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                  );
                }) : (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      Žiadne dobropisy
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Dialog pre zobrazenie dobropisu */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detail dobropisu</DialogTitle>
            <DialogDescription>
              Náhľad a export dobropisu
            </DialogDescription>
            <div className="flex space-x-2">
              <Button
                variant="outline"
                onClick={handlePDFExport}
                disabled={!selectedDobropis}
                className="flex items-center"
              >
                <Download className="h-4 w-4 mr-2" />
                Stiahnuť PDF
              </Button>
            </div>
          </DialogHeader>
          {selectedDobropis && (
            <div ref={(el) => dobropisPreviewRef[1](el)} id="dobropis-preview">
              <CreditNotePreview
                creditNote={selectedDobropis}
                client={(clients as any)?.find((c: any) => c.id === selectedDobropis.clientId)}
                user={userData}
                printMode={false}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}